import CryptoJS from 'crypto-js';
import { push, ref, remove } from 'firebase/database';
import { database } from '../firebase';

const OUTBOX_NAME = 'nationalizationRegulatoryImportOutbox';
const outboxPath = (companyUID) => `companies/${companyUID}/${OUTBOX_NAME}`;

export const REGULATORY_IMPORT_SCOPES = Object.freeze({
  FULL_WORKFORCE: 'full_workforce',
  PARTIAL: 'partial'
});

// CSV headers stay stable in English, but cell values may be English or Arabic.
// Normalize Arabic marks before matching so values such as `عُمان` and `عمان`
// resolve to the same regulator code without guessing unknown text.
const normalizeCsvToken = (value) => String(value ?? '')
  .trim()
  .toLowerCase()
  .replace(/[\u064B-\u065F\u0670\u0640]/g, '')
  // Fold Arabic alef/hamza forms so Arabic CSV values match the canonical
  // aliases regardless of normal keyboard spelling (إجازة, الإمارات, etc.).
  .replace(/[أإآٱ]/g, 'ا')
  .replace(/[-\s]+/g, '_');

const ARABIC_EMPLOYMENT_STATUS_ALIASES = Object.freeze({
  'نشط': 'active',
  'فعال': 'active',
  'على_راس_العمل': 'active',
  'في_فترة_التجربة': 'probation',
  'تحت_التجربة': 'probation',
  'في_اجازة': 'on_leave',
  'اجازة': 'on_leave',
  'اجازة_مدفوعة': 'paid_leave',
  'اجازة_امومة': 'maternity_leave',
  'اجازة_ابوة': 'paternity_leave',
  'قيد_انهاء_الخدمة': 'offboarding',
  'قيد_التهيئة': 'onboarding',
  'قيد_الانضمام': 'onboarding',
  'قبل_المباشرة': 'preboarding',
  'تم_التعيين': 'hired',
  'مقبول': 'accepted',
  'تم_قبول_العرض': 'offer_accepted',
  'بانتظار_المباشرة': 'pending_start',
  'غير_نشط': 'inactive',
  'تم_انهاء_الخدمة': 'offboarded',
  'منتهية_خدمته': 'terminated',
  'ملغى': 'cancelled',
  'ملغاة': 'cancelled'
});

export function normalizeRegulatoryEmploymentStatus(value = '') {
  const normalized = normalizeCsvToken(value);
  return ARABIC_EMPLOYMENT_STATUS_ALIASES[normalized] || normalized;
}

export function normalizeRegulatoryCountryCode(value = '') {
  const normalized = normalizeCsvToken(value);
  const aliases = {
    ae: 'AE',
    uae: 'AE',
    united_arab_emirates: 'AE',
    'الامارات': 'AE',
    'الامارات_العربية_المتحدة': 'AE',
    sa: 'SA',
    ksa: 'SA',
    saudi: 'SA',
    saudi_arabia: 'SA',
    'السعودية': 'SA',
    'المملكة_العربية_السعودية': 'SA',
    qa: 'QA',
    qatar: 'QA',
    'قطر': 'QA',
    'دولة_قطر': 'QA',
    om: 'OM',
    oman: 'OM',
    'عمان': 'OM',
    'سلطنة_عمان': 'OM',
    bh: 'BH',
    bahrain: 'BH',
    'البحرين': 'BH',
    'مملكة_البحرين': 'BH',
    kw: 'KW',
    kuwait: 'KW',
    'الكويت': 'KW',
    'دولة_الكويت': 'KW'
  };
  return aliases[normalized] || String(value || '').trim().toUpperCase();
}

export function parseRegulatoryBoolean(value) {
  const normalized = normalizeCsvToken(value);
  if (!normalized) return null;
  const trueValues = new Set([
    'true', 'yes', 'y', '1', 'verified', 'documented', 'registered', 'valid',
    'نعم', 'اجل', 'صحيح', 'موثق', 'مسجل', 'ساري', 'صالح', 'مؤكد'
  ]);
  const falseValues = new Set([
    'false', 'no', 'n', '0', 'unverified', 'undocumented', 'not_registered', 'invalid',
    'لا', 'كلا', 'خطا', 'غير_موثق', 'غير_مسجل', 'غير_ساري', 'غير_صالح'
  ]);
  if (trueValues.has(normalized)) return true;
  if (falseValues.has(normalized)) return false;
  return null;
}

// Keep row-identity validation independent from the CSV parser so it is easy to
// test the "all rows or no rows" regulatory-import rule.
export function getMissingRegulatoryImportIdentityFields({
  name,
  employeeId,
  employmentStatus
} = {}) {
  return [
    !String(name || '').trim() ? 'name' : null,
    !String(employeeId || '').trim() ? 'employeeId' : null,
    !String(employmentStatus || '').trim() ? 'employmentStatus' : null
  ].filter(Boolean);
}

export function shouldRejectCsvParserErrors({ errors = [], recordCount = 0, regulatory = false } = {}) {
  return Array.isArray(errors)
    && errors.length > 0
    && (regulatory || Number(recordCount) === 0);
}

export function resolveRegulatoryImportEstablishmentId({
  suppliedEstablishmentId,
  eligibleEstablishments = []
} = {}) {
  const supplied = String(suppliedEstablishmentId || '').trim();
  if (supplied) return supplied;
  const eligible = (Array.isArray(eligibleEstablishments) ? eligibleEstablishments : [])
    .filter((establishment) => String(establishment?.id || '').trim());
  // A single legal establishment is unambiguous. With multiple branches every
  // row must name its own establishment so no denominator is silently shifted.
  return eligible.length === 1 ? String(eligible[0].id).trim() : '';
}

export function normalizeRegulatoryImportEmployeeIds(employeeIds = []) {
  return Array.from(new Set(
    (Array.isArray(employeeIds) ? employeeIds : [])
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  )).sort();
}

export function hashRegulatoryImportEmployeeIds(employeeIds = []) {
  const sortedEmployeeIds = normalizeRegulatoryImportEmployeeIds(employeeIds);
  return CryptoJS.SHA256(JSON.stringify(sortedEmployeeIds)).toString(CryptoJS.enc.Hex);
}

export function createRegulatoryImportFactsBindingToken(
  cryptoSource = (typeof window !== 'undefined' ? window.crypto : null)
) {
  if (!cryptoSource || typeof cryptoSource.getRandomValues !== 'function') {
    throw new Error('Secure browser randomness is required for a regulatory import.');
  }
  const bytes = new Uint8Array(32);
  cryptoSource.getRandomValues(bytes);
  // Hex is path/body safe and reveals nothing about the low-entropy legal facts.
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
}

export function buildCanonicalRegulatoryImportChunks(employees = [], chunkSize = 1000) {
  const safeChunkSize = Math.max(1, Number(chunkSize) || 1000);
  const sorted = [...(Array.isArray(employees) ? employees : [])].sort((left, right) => {
    const leftId = String(left?.employee_id || '');
    const rightId = String(right?.employee_id || '');
    if (leftId === rightId) return 0;
    return leftId < rightId ? -1 : 1;
  });
  const chunks = [];
  for (let offset = 0; offset < sorted.length; offset += safeChunkSize) {
    chunks.push(sorted.slice(offset, offset + safeChunkSize));
  }
  return chunks;
}

export function allocateRegulatoryImportId(companyUID) {
  if (!companyUID) throw new Error('A company is required for a regulatory import.');
  const importId = push(ref(database, outboxPath(companyUID))).key;
  if (!importId) throw new Error('Could not allocate a regulatory import ID.');
  return importId;
}

export function addRegulatoryImportMarkerToUpdates(updates, {
  companyUID,
  importId,
  expectedEmployeeIdsHash,
  factsBindingToken,
  importScope,
  fullWorkforceAttested,
  expectedEmployeeCount,
  expectedChunkCount,
  queuedAt = Date.now()
}) {
  if (!companyUID || !importId || !expectedEmployeeIdsHash || !factsBindingToken) {
    throw new Error('The regulatory import marker is incomplete.');
  }
  if (!Object.values(REGULATORY_IMPORT_SCOPES).includes(importScope)) {
    throw new Error('The regulatory import scope is invalid.');
  }
  if (
    (importScope === REGULATORY_IMPORT_SCOPES.FULL_WORKFORCE && fullWorkforceAttested !== true)
    || (importScope === REGULATORY_IMPORT_SCOPES.PARTIAL && fullWorkforceAttested !== false)
  ) {
    throw new Error('The regulatory import attestation does not match its scope.');
  }
  const marker = {
    schemaVersion: 1,
    importId,
    expectedEmployeeIdsHash,
    factsBindingToken,
    importScope,
    fullWorkforceAttested,
    expectedEmployeeCount: Number(expectedEmployeeCount),
    expectedChunkCount: Number(expectedChunkCount),
    queuedAt: Number(queuedAt)
  };
  // This marker omits the employee ID list and all legal fact values. Its roster
  // count/hash still describe sensitive HR activity, so RTDB rules must restrict it.
  updates[`${outboxPath(companyUID)}/${importId}`] = marker;
  return marker;
}

export async function removeRegulatoryImportMarker(companyUID, importId) {
  if (!companyUID || !importId) return;
  await remove(ref(database, `${outboxPath(companyUID)}/${importId}`));
}
